// (c) chaehongee. Confidential and proprietary.

import { buildApiUrl } from '../../../services/http'
import type { Rect, Size } from '../types'

export interface LayerExportData {
    sourceUrl: string
    imageRect: Rect // Current rect in world space
    imageNaturalSize: Size
    initialImageRect?: Rect // Base rect for calculating scale
    zIndex: number
    overlayBlob?: Blob | null // Optional drawing overlay for this layer
}

export interface ExportMultiLayerParams {
    frameRect: Rect // The cropping container
    layers: LayerExportData[]
}

// Keeping legacy interface for backward compatibility if needed, 
// but implementation will map to multi-layer logic.
export interface ExportCroppedImageParams {
    sourceUrl: string
    imageRect: Rect
    frameRect: Rect
    imageNaturalSize: Size
    initialImageRect?: Rect
}

interface ExportDimensions {
    // Output canvas dimensions (determined by frame size in natural pixels)
    outW: number
    outH: number
    
    // Per-layer drawing parameters
    sx: number
    sy: number
    sw: number
    sh: number
    dx: number
    dy: number
    dw: number
    dh: number
    
    // Scaling info used for decision making
    worldToNaturalX: number
    worldToNaturalY: number
}

function calculateLayerDimensions(
    frameRect: Rect,
    layer: LayerExportData,
    baseScaleReferenceRect?: Rect
): ExportDimensions {
    const { imageRect, imageNaturalSize, initialImageRect } = layer

    // WYSIWYG Export Strategy:
    // 1. Frame dimensions determine output canvas size (in natural pixels)
    // 2. Image is drawn at its transformed size/position within that canvas
    // 3. Preserve the visual relationship - no stretching to fill

    // Step 1: Calculate world-to-natural scale.
    // Use the layer's initial rect if available, otherwise current.
    // CRITICAL: If exporting multiple layers, we need a consistent "World Unit" definition.
    // Usually, the primary layer (or the frame's base rect) defines the pixel density.
    // If baseScaleReferenceRect is provided, use it to normalize pixel density.
    
    const baseRect = baseScaleReferenceRect || initialImageRect || imageRect
    
    // Safety check for zero-width/height to avoid division by zero
    const safeBaseW = baseRect.w || 1
    const safeBaseH = baseRect.h || 1
    
    const worldToNaturalX = imageNaturalSize.w / safeBaseW
    const worldToNaturalY = imageNaturalSize.h / safeBaseH

    // Step 2: Output canvas dimensions = frame size in natural pixels
    // This assumes the frame exists in the same "World Space" as the baseRect
    const outW = Math.max(1, Math.round(frameRect.w * worldToNaturalX))
    const outH = Math.max(1, Math.round(frameRect.h * worldToNaturalY))

    // Step 4: Calculate image position relative to frame (in world coordinates)
    const imageOffsetXInFrame = imageRect.x - frameRect.x
    const imageOffsetYInFrame = imageRect.y - frameRect.y

    // Step 5: Calculate image dimensions in output space (scaled)
    const imageWidthInOutput = imageRect.w * worldToNaturalX
    const imageHeightInOutput = imageRect.h * worldToNaturalY

    // Step 6: Calculate image position in output canvas pixels
    const imageXInOutput = imageOffsetXInFrame * worldToNaturalX
    const imageYInOutput = imageOffsetYInFrame * worldToNaturalY

    // Step 7: Determine the visible region of the image in output space
    // Intersection of image bounds with canvas bounds
    const visibleLeft = Math.max(0, imageXInOutput)
    const visibleTop = Math.max(0, imageYInOutput)
    const visibleRight = Math.min(outW, imageXInOutput + imageWidthInOutput)
    const visibleBottom = Math.min(outH, imageYInOutput + imageHeightInOutput)

    const visibleWidth = Math.max(0, visibleRight - visibleLeft)
    const visibleHeight = Math.max(0, visibleBottom - visibleTop)

    // Step 8: Map the visible region back to source natural image coordinates
    // Avoid division by zero if image is collapsed
    const safeImgW = imageWidthInOutput || 1
    const safeImgH = imageHeightInOutput || 1
    
    const srcStartX = (visibleLeft - imageXInOutput) / safeImgW
    const srcStartY = (visibleTop - imageYInOutput) / safeImgH
    const srcWidthRatio = visibleWidth / safeImgW
    const srcHeightRatio = visibleHeight / safeImgH

    // Convert ratios to natural image pixels
    const sx = Math.round(srcStartX * imageNaturalSize.w)
    const sy = Math.round(srcStartY * imageNaturalSize.h)
    const sw = Math.max(1, Math.round(srcWidthRatio * imageNaturalSize.w))
    const sh = Math.max(1, Math.round(srcHeightRatio * imageNaturalSize.h))

    // Step 9: Draw parameters
    const dx = Math.round(visibleLeft)
    const dy = Math.round(visibleTop)
    const dw = Math.round(visibleWidth)
    const dh = Math.round(visibleHeight)

    return {
        outW, outH,
        sx, sy, sw, sh,
        dx, dy, dw, dh,
        worldToNaturalX,
        worldToNaturalY
    }
}

async function loadImageFromUrl(sourceUrl: string): Promise<HTMLImageElement | null> {
    // Handle data URLs directly
    if (sourceUrl.startsWith('data:')) {
        return new Promise((resolve) => {
             const img = new Image()
             img.onload = () => resolve(img)
             img.onerror = () => resolve(null)
             img.src = sourceUrl
        })
    }

    try {
        // Use proxy to ensure we can get the image data (CORS, etc)
        // Correct path is /system/proxy_image based on openapi.ts
        const proxied = buildApiUrl(`/system/proxy_image?url=${encodeURIComponent(sourceUrl)}`)
        
        // Use fetch to get blob - this avoids canvas tainting issues better than crossOrigin img
        const resp = await fetch(proxied)
        if (!resp.ok) throw new Error(`Proxy fetch failed: ${resp.status}`)
        
        const blob = await resp.blob()
        const url = URL.createObjectURL(blob)
        
        return new Promise((resolve) => {
            const img = new Image()
            img.onload = () => { URL.revokeObjectURL(url); resolve(img) }
            img.onerror = () => { URL.revokeObjectURL(url); resolve(null) }
            img.src = url
        })
    } catch (e) {
        console.warn('Proxy load failed, falling back to direct load', e)
        // Fallback: direct load (might taint canvas if CORS missing)
        return new Promise((resolve) => {
            const img = new Image()
            img.crossOrigin = 'anonymous' // Try to request CORS
            img.onload = () => resolve(img)
            img.onerror = () => resolve(null)
            img.src = sourceUrl
        })
    }
}

/**
 * Core export function supporting multiple layers
 */
export async function exportMultiLayerFrame(params: ExportMultiLayerParams): Promise<{ blob: Blob; width: number; height: number } | null> {
    const { frameRect, layers } = params
    if (layers.length === 0) return null

    // Sort layers by Z-Index
    const sortedLayers = [...layers].sort((a, b) => a.zIndex - b.zIndex)

    // Determine output canvas size based on the PRIMARY layer (usually the bottom one or the one matching frame ID)
    // For consistency, we use the first layer's natural scale to define the "resolution" of the export.
    // If the first layer is very low res, the export might be pixelated.
    // TODO: Consider a "target resolution" or "max resolution" strategy.
    const primaryLayer = sortedLayers[0] 
    
    // We calculate the dimensions just to get the output size (outW, outH)
    const masterDims = calculateLayerDimensions(frameRect, primaryLayer)
    
    const canvas = document.createElement('canvas')
    canvas.width = masterDims.outW
    canvas.height = masterDims.outH
    const ctx = canvas.getContext('2d')
    if (!ctx) return null

    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'

    // Draw each layer
    for (const layer of sortedLayers) {
        // Recalculate dimensions for this specific layer relative to the Master Frame size
        // Passing primaryLayer.initialImageRect (or rect) as the "World Unit Reference" ensures
        // that 1 world unit = X natural pixels is consistent across all layers?
        // Actually, no. Each image has its OWN natural size.
        // But the Frame Size (canvas.width) was established by the Primary Layer's pixel density.
        // We need to render Secondary Layer (Natural Size B) into Canvas (Natural Size A).
        
        // Re-eval logic:
        // masterDims.outW = frameRect.w * (primary.natural.w / primary.base.w)
        // 
        // For Layer B:
        // We need to map Layer B's world rect to the Output Canvas pixels.
        // World -> Canvas Scale Factor is defined by the Primary Layer.
        // ScaleX = masterDims.worldToNaturalX
        
        const { imageRect, imageNaturalSize } = layer
        
        // Calculate image position in Output Canvas Pixels
        const imageOffsetX = imageRect.x - frameRect.x
        const imageOffsetY = imageRect.y - frameRect.y
        
        const imageX = imageOffsetX * masterDims.worldToNaturalX
        const imageY = imageOffsetY * masterDims.worldToNaturalY
        const imageW = imageRect.w * masterDims.worldToNaturalX
        const imageH = imageRect.h * masterDims.worldToNaturalY

        // Clipping logic (Output Space)
        const visibleL = Math.max(0, imageX)
        const visibleT = Math.max(0, imageY)
        const visibleR = Math.min(canvas.width, imageX + imageW)
        const visibleB = Math.min(canvas.height, imageY + imageH)
        
        const visibleW = Math.max(0, visibleR - visibleL)
        const visibleH = Math.max(0, visibleB - visibleT)

        if (visibleW === 0 || visibleH === 0) continue

        // Source Sampling Logic (Input Space)
        // Map visible region back to Image Natural Pixels
        // Ratio of visible region within the drawn image rect
        const safeW = imageW || 1
        const safeH = imageH || 1
        
        const rX = (visibleL - imageX) / safeW
        const rY = (visibleT - imageY) / safeH
        const rW = visibleW / safeW
        const rH = visibleH / safeH
        
        const sx = Math.round(rX * imageNaturalSize.w)
        const sy = Math.round(rY * imageNaturalSize.h)
        const sw = Math.max(1, Math.round(rW * imageNaturalSize.w))
        const sh = Math.max(1, Math.round(rH * imageNaturalSize.h))

        const dx = Math.round(visibleL)
        const dy = Math.round(visibleT)
        const dw = Math.round(visibleW)
        const dh = Math.round(visibleH)

        // Load & Draw
        try {
            const img = await loadImageFromUrl(layer.sourceUrl)
            if (img) {
                ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh)
            }
            
            // Draw Overlay if present
            if (layer.overlayBlob) {
                const overlayImg = await new Promise<HTMLImageElement | null>((resolve) => {
                    const url = URL.createObjectURL(layer.overlayBlob!)
                    const i = new Image()
                    i.onload = () => { URL.revokeObjectURL(url); resolve(i) }
                    i.onerror = () => { URL.revokeObjectURL(url); resolve(null) }
                    i.src = url
                })
                
                // Overlay assumption: The blob generated by DrawOverlay matches the visual bounds of the LAYER,
                // or the visual bounds of the FRAME?
                // In `DrawOverlay.tsx` (and Konva), drawing is usually relative to the container (Frame).
                // If DrawOverlay is frame-relative, we just draw it at 0,0 of the canvas?
                // IF the overlay was exported matching the frame size.
                // Re-verifying DrawOverlay export logic is needed, but assuming standard behavior:
                // If it overlays the whole frame, draw at 0,0.
                if (overlayImg) {
                    // Check logic: usually overlays are generated matching the export dimensions requested.
                    // If we pass it in, we assume it's pre-sized?
                    // For now, draw full canvas.
                    ctx.drawImage(overlayImg, 0, 0, canvas.width, canvas.height)
                }
            }
        } catch (e) {
            console.error('Failed to draw layer', layer.sourceUrl, e)
        }
    }

    const finalBlob = await new Promise<Blob | null>((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'))
    if (!finalBlob) return null
    return { blob: finalBlob, width: canvas.width, height: canvas.height }
}

// Backward compatible wrapper
export async function exportCroppedImage(params: ExportCroppedImageParams): Promise<{ blob: Blob; width: number; height: number } | null> {
    return exportMultiLayerFrame({
        frameRect: params.frameRect,
        layers: [{
            sourceUrl: params.sourceUrl,
            imageRect: params.imageRect,
            imageNaturalSize: params.imageNaturalSize,
            initialImageRect: params.initialImageRect,
            zIndex: 0
        }]
    })
}

// Backward compatible wrapper with overlay
export async function exportCroppedImageWithOverlay(
    params: ExportCroppedImageParams,
    overlayPng: Blob | null
): Promise<{ blob: Blob; width: number; height: number } | null> {
    return exportMultiLayerFrame({
        frameRect: params.frameRect,
        layers: [{
            sourceUrl: params.sourceUrl,
            imageRect: params.imageRect,
            imageNaturalSize: params.imageNaturalSize,
            initialImageRect: params.initialImageRect,
            zIndex: 0,
            overlayBlob: overlayPng
        }]
    })
}
